import React, { useState, useEffect } from "react";
import "./Portal.css";
import { useAuth } from "../../AuthContext";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import upload from "../../img/arrow.png";
import { PaymentWidget } from "./PaymentWidget";
import CustomDropdown from "./CustomDropdown";
import { ReactComponent as Refresh } from "../../icons/refresh.svg";
import { Table } from "../../lv1ui";

const apiItems = [
    { title: "WhatsApp", id: 0, priceField: "price_wa" },
    { title: "Telegram", id: 1, priceField: "price_telegram" },
    { title: "IMO", id: 2, priceField: "price_imo" },
    { title: "Viber", id: 3, priceField: "price_viber" }
];

export function Portal() {
    const [currentPriceItem, setCurrentPriceItem] = React.useState(null);
    const [mode, setMode] = useState("manual");
    const [csvFile, setCsvFile] = useState(null);
    const [phones, setPhones] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState("");
    const [statuses, setStatuses] = useState([]);

    const [isLoading, setLoading] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [modeView, setModeView] = useState("api"); // "api" | "list"

    const { accessToken, user, loading, fetchWithAuth } = useAuth();
    const { t } = useTranslation();

    const PAGE_SIZE_OPTIONS = [10, 20, 30];
    const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
        totalPages: 0,
    });

    const [msgFilters, setMsgFilters] = useState({
        type: "",
        status: "",
        date_from: "",
        date_to: "",
        phone: "",
    });

    function buildQuery(params) {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && String(v).trim() !== "") {
                usp.append(k, v);
            }
        });
        return usp.toString();
    }

    const apiCampaignOptions = [
        user?.api_campaign_wa_id
            ? {
                  label: "WhatsApp API Campaign",
                  value: String(user.api_campaign_wa_id),
              }
            : null,
        user?.api_campaign_telegram_id
            ? {
                  label: "Telegram API Campaign",
                  value: String(user.api_campaign_telegram_id),
              }
            : null,
    ].filter(Boolean);

    const getDefaultApiCampaignId = () => {
        if (user?.api_campaign_wa_id) return String(user.api_campaign_wa_id);
        if (user?.api_campaign_telegram_id) return String(user.api_campaign_telegram_id);
        return "";
    };

    const fetchCampaigns = async () => {
        try {
            const res = await fetchWithAuth("/api/web/campaigns");
            const data = await res.json();
            if (data.success) setCampaigns(data.campaigns);
        } catch (err) {
            console.error("Error loading campaigns", err);
        }
    };

    useEffect(() => {
        const load = async () => {
            await fetchCampaigns();
        };
        load();
    }, []);

    useEffect(() => {
        if (user && !currentPriceItem) {
            const firstEnabled = apiItems.find(item => user[item.title.toLowerCase()]);
            if (firstEnabled) {
                setCurrentPriceItem(firstEnabled);
            }
        }
    }, [user, currentPriceItem]);

    useEffect(() => {
        if (modeView === "api") {
            const defaultId = getDefaultApiCampaignId();
            setSelectedCampaignId(defaultId);
            setStatuses([]);
            setPagination(prev => ({ ...prev, page: 1 }));
        }

        if (modeView === "list") {
            setSelectedCampaignId("");
            setStatuses([]);
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    }, [modeView, user]);

    useEffect(() => {
        if (selectedCampaignId) {
            fetchStatuses(selectedCampaignId, pagination.page, pagination.pageSize);
        } else {
            setStatuses([]);
        }
    }, [selectedCampaignId, pagination.page, pagination.pageSize]);

    if (loading) return <div>Loading...</div>;
    if (!accessToken) return <Navigate to="/login" replace />;
    if (!user) return <div>Loading user data...</div>;
    if (!currentPriceItem) return <div>Loading channels...</div>;

    const fetchStatuses = async (campaignId, page = 1, limit = DEFAULT_PAGE_SIZE) => {
        try {
            const qs = buildQuery({
                page,
                limit,
                ...msgFilters,
            });

            const res = await fetchWithAuth(`/api/web/campaign/${campaignId}?${qs}`);
            const data = await res.json();

            if (data.success) {
                setStatuses(data.data);
                setPagination({
                    page: data.page,
                    pageSize: data.pageSize,
                    total: data.total,
                    totalPages: data.totalPages,
                });
            }
        } catch (err) {
            console.error("Error while fetching messages:", err);
        }
    };

    const parsePhones = async () => {
        if (mode === "manual") {
            return phones
                .split(/[\n,;]/)
                .map(p => p.replace(/\s+/g, "").trim())
                .filter(p => /^\+?\d+$/.test(p));
        }

        if (mode === "csv" && csvFile) {
            const text = await csvFile.text();
            return text
                .split(/\r?\n/)
                .map(p => p.replace(/\s+/g, "").trim())
                .filter(p => /^\+?\d+$/.test(p));
        }

        return [];
    };

    const handleMessageSubmit = async () => {
        setError(null);
        setSuccess(null);

        const phoneList = await parsePhones();
        if (!message || phoneList.length === 0) {
            setError("Please provide a message and at least one phone number");
            return;
        }

        setLoading(true);

        try {
            const res = await fetchWithAuth("/api/web/campaigns/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    phones: phoneList,
                    message,
                    name: "Campaign " + Date.now(),
                    type: currentPriceItem?.title.toLowerCase(),
                }),
            });

            if (!res) {
                setError("No response from server");
                return;
            }

            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setSuccess("Messages sent successfully!");
                setMessage("");
                setPhones("");
                setCsvFile(null);
                await fetchCampaigns();
                window.dispatchEvent(new Event("force-balance-reload"));
            } else {
                setError(data.error || "Failed to send SMS");
            }
        } catch (err) {
            console.error(err);
            setError("An error occurred while sending the SMS");
        } finally {
            setLoading(false);
        }
    };

    const reloadMessages = async () => {
        if (reloading) return;
        setReloading(true);

        await Promise.all([
            fetchStatuses(selectedCampaignId, pagination.page, pagination.pageSize),
            new Promise(res => setTimeout(res, 500)),
        ]);

        setReloading(false);
    };

    const selectedCampaign = campaigns?.find(c => c.id === Number(selectedCampaignId));

    const msgColumns = [
        { key: "id", label: "#" },
        { key: "dst_addr", label: "Phone number" },
        { key: "status", label: "Status" },
        { key: "price", label: "Price" },
        { key: "created_at", label: "Created (UTC)" },
        { key: "status_updated_at", label: "Updated (UTC)" },
        { key: "field_1", label: "Field 1" },
        { key: "text", label: "Text" },
    ];

    const msgRows = statuses.map((s) => ({
        ...s,
        price: `$${s.price}`,
        created_at: new Date(s.created_at).toLocaleString("en-GB", { timeZone: "UTC" }),
        status_updated_at: new Date(s.status_updated_at).toLocaleString("en-GB", { timeZone: "UTC" }),
        dst_addr: s.dst_addr ? <span title={s.dst_addr}>{s.dst_addr}</span> : "",
        text: s.text ? <span title={s.text}>{`${s.text.slice(0, 30)}...`}</span> : "",
    }));

    const handleExportXlsx = async () => {
        if (!selectedCampaignId) return;

        try {
            const res = await fetchWithAuth(`/api/web/campaign/${selectedCampaignId}/export?format=xlsx`, {
                method: "GET",
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || "Failed to export file");
                return;
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const today = new Date().toISOString().slice(0, 10);

            a.href = url;
            a.download = `campaign_${selectedCampaignId}_${today}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("export error", e);
            setError("Export error");
        }
    };

    return (
        <div className="portalPage">
            <div className="sendContainer">
                <div className={"portalHeader"}>Created campaigns:</div>

                <div style={{ marginBottom: 10 }}>
                    <label>
                        <input
                            type="radio"
                            value="api"
                            checked={modeView === "api"}
                            onChange={() => {
                                setModeView("api");
                                const defaultId = getDefaultApiCampaignId();
                                setSelectedCampaignId(defaultId);
                            }}
                        />
                        API Campaign
                    </label>

                    <label style={{ marginLeft: 20 }}>
                        <input
                            type="radio"
                            value="list"
                            checked={modeView === "list"}
                            onChange={() => {
                                setModeView("list");
                                setSelectedCampaignId("");
                            }}
                        />
                        Campaigns List
                    </label>
                </div>

                {modeView === "api" && (
                    <CustomDropdown
                        value={selectedCampaignId}
                        onChange={(newBlockId) => {
                            setSelectedCampaignId(newBlockId);
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                        options={[
                            { label: "— Select API campaign —", value: "" },
                            ...apiCampaignOptions,
                        ]}
                        fullWidth
                    />
                )}

                {modeView === "list" && (
                    <CustomDropdown
                        value={selectedCampaignId}
                        onChange={newBlockId => {
                            setSelectedCampaignId(newBlockId);
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                        options={[
                            { label: "— Select campaign —", value: "" },
                            ...campaigns
                                .filter(c =>
                                    c.id !== user?.api_campaign_wa_id &&
                                    c.id !== user?.api_campaign_telegram_id
                                )
                                .map(c => ({
                                    label: `${c.name} • ${c.channel} • ${new Date(c.created_at).toLocaleDateString()}`,
                                    value: c.id
                                }))
                        ]}
                        fullWidth
                    />
                )}

                {modeView === "list" && selectedCampaign && (
                    <div>
                        <label className="labelP">Message text:</label>
                        <div className="messageText">{selectedCampaign.message}</div>
                    </div>
                )}

                {selectedCampaignId && (
                    <div>
                        <div className="messageList">
                            <label className="labelP">Message list:</label>

                            <div className={"reloadBalanceContainer"}>
                                <div
                                    onClick={reloadMessages}
                                    className={reloading ? "reloadBalanceSpin" : "reloadBalance"}
                                >
                                    <Refresh />
                                </div>
                            </div>

                            <button
                                style={{ marginLeft: 12 }}
                                onClick={handleExportXlsx}
                                disabled={!selectedCampaignId}
                                className="startCampaignButton"
                            >
                                <div className="buttonMode">Export INFO</div>
                            </button>
                        </div>

                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                            <select
                                value={msgFilters.type}
                                onChange={e => setMsgFilters(f => ({ ...f, type: e.target.value }))}
                            >
                                <option value="">Type: any</option>
                                <option value="whatsapp">whatsapp</option>
                                <option value="telegram">telegram</option>
                                <option value="viber">viber</option>
                                <option value="imo">imo</option>
                            </select>

                            <select
                                value={msgFilters.status}
                                onChange={e => setMsgFilters(f => ({ ...f, status: e.target.value }))}
                            >
                                <option value="">Status: any</option>
                                <option value="checkwa">checkwa</option>
                                <option value="sent">sent</option>
                                <option value="delivered">delivered</option>
                                <option value="failed">failed</option>
                                <option value="undelivered">undelivered</option>
                                <option value="not_in_wa">not_in_wa</option>
                                <option value="expired">expired</option>
                            </select>

                            <input
                                type="date"
                                value={msgFilters.date_from}
                                onChange={e => setMsgFilters(f => ({ ...f, date_from: e.target.value }))}
                            />

                            <input
                                type="date"
                                value={msgFilters.date_to}
                                onChange={e => setMsgFilters(f => ({ ...f, date_to: e.target.value }))}
                            />

                            <input
                                placeholder="Phone contains…"
                                value={msgFilters.phone}
                                onChange={e => setMsgFilters(f => ({ ...f, phone: e.target.value }))}
                            />

                            <button onClick={() => fetchStatuses(selectedCampaignId, 1, pagination.pageSize)}>
                                Apply
                            </button>

<button
  onClick={() => {
    const empty = {
      type: '',
      status: '',
      date_from: '',
      date_to: '',
      phone: '',
    };
    setMsgFilters(empty);

    const qs = new URLSearchParams({
      page: '1',
      limit: String(pagination.pageSize),
    }).toString();

    fetchWithAuth(`/api/web/campaign/${selectedCampaignId}?${qs}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setStatuses(data.data);
          setPagination({
            page: data.page,
            pageSize: data.pageSize,
            total: data.total,
            totalPages: data.totalPages,
          });
        }
      });
  }}
>
  Reset
</button>
                        </div>

                        <Table height="400px" columns={msgColumns} rows={msgRows} />

                        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => fetchStatuses(selectedCampaignId, pagination.page - 1, pagination.pageSize)}
                            >
                                ← Prev
                            </button>

                            <span>
                                Page {pagination.page} of {pagination.totalPages} ({pagination.total} messages)
                            </span>

                            <button
                                disabled={pagination.page === pagination.totalPages}
                                onClick={() => fetchStatuses(selectedCampaignId, pagination.page + 1, pagination.pageSize)}
                            >
                                Next →
                            </button>

                            <select
                                value={pagination.pageSize}
                                onChange={(e) => fetchStatuses(selectedCampaignId, 1, Number(e.target.value))}
                            >
                                {PAGE_SIZE_OPTIONS.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className={"portalHeader"}>Create campaign</div>

                <div className={"sendItems"}>
                    {apiItems.map((item, index) => {
                        const isDisabled = !user[item.title.toLowerCase()];
                        const className =
                            currentPriceItem?.id === item.id
                                ? "selectedSendItem"
                                : isDisabled
                                    ? "sendItem disabledSendItem"
                                    : "sendItem";

                        return (
                            <div
                                key={index}
                                className={className}
                                onClick={() => {
                                    setCurrentPriceItem(item);
                                }}
                            >
                                {item.title}
                            </div>
                        );
                    })}
                </div>

                <div className="modeItems">
                    <div className={mode === "manual" ? "selectedSendItem" : "sendItem"} onClick={() => setMode("manual")}>
                        Enter numbers manually
                    </div>
                    <div className={mode === "csv" ? "selectedSendItem" : "sendItem"} onClick={() => setMode("csv")}>
                        Upload CSV
                    </div>
                </div>

                {mode === "manual" ? (
                    <div className="phoneField">
                        <div className="labelP">
                            Phone list
                        </div>
                        <div className="blockGeneratorContentTextareaBlock">
                            <textarea
                                className="blockGeneratorContentTextareaBlockTextarea"
                                placeholder="Enter numbers separated by commas or line breaks (digits only)"
                                value={phones}
                                onChange={(e) => setPhones(e.target.value)}
                                disabled={isLoading}
                            />
                            <div
                                style={{
                                    fontSize: "0.9em",
                                    marginTop: 5,
                                    whiteSpace: "normal",
                                    wordWrap: "breakWord",
                                    width: "100%",
                                    color: "#BABABA"
                                }}
                            >
                                Example: <code style={{ backgroundColor: "transparent", color: "#BABABA" }}>1234567890, 9876543210, 5556667777</code>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="phoneField">
                        <label className="labelP">
                            Phone list
                        </label>
                        <label className="customFileUpload">
                            <img alt="upload" className="imageU" src={upload} />
                            <div className="fileName">
                                {csvFile ? csvFile.name : "Select file"}
                            </div>
                            <div className="fileFormat">
                                CSV format
                            </div>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={(e) => {
                                    if (e.target.files.length > 0) {
                                        setCsvFile(e.target.files[0]);
                                    }
                                }}
                                disabled={isLoading}
                            />
                        </label>
                    </div>
                )}

                <div className={"messageContainer"}>
                    <label className="labelP">
                        Message text
                    </label>
                    <textarea
                        className="blockGeneratorContentTextareaBlockTextarea"
                        placeholder="Message text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                <div className="priceCentered">
                    Cost per message: ${+user[apiItems[currentPriceItem.id].priceField]}
                </div>

                <button onClick={handleMessageSubmit} className="startCampaignButton" disabled={isLoading}>
                    <div className="buttonMode">{isLoading ? "Sending" : "Create campaign"}</div>
                </button>

                {error && <div className="loginError" style={{ color: "#268DFF" }}>{error}</div>}
                {success && <div className="loginError" style={{ color: "#268DFF" }}>{success}</div>}

                <PaymentWidget />
            </div>
        </div>
    );
}
